import { useSyncExternalStore } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fehler, sende } from "./klient";
import { ablageHochladen } from "./routen";
import { FACH_WARTESCHLANGE, alle, entfernen, schreiben, uuid } from "./speicher";
import { stehtSchon } from "./doppelt";

/* ══════════════════════════════════════════════════════════════════
   Die Warteschlange — was im Funkloch entsteht und später hinausgeht.

   09-mobile.md: *„Auf dem Hof ist Empfang, in der Kiesgrube nicht."*

   **Diese Gegenstände gehen diesen Weg:** das Übergabe- und
   Rücknahmeprotokoll samt Fotos und Unterschrift, die Vorbereitung
   (mit ihrer Notiz und ihren Fotos), das Einstempeln, das Ausstempeln
   und seit dem 17.09.2026 die **Quittung** über die Stunden eines
   Einsatztags (0117) — sie trägt eine Unterschrift wie das Protokoll,
   und ein zweites Mal unterschreibt der Polier nicht. Mit dem
   Einsatzschirm kamen drei dazu: der **Phasenwechsel** (losfahren, auf
   der Baustelle), der **Zählerstand** des Tages und das **Foto** als
   eigene Zeile. Am Abend des 17.09.2026 der **Schaden** bei der Rücknahme
   (`defekt`, 0125) — bis dahin die eine Eintragung des Einsatzes, die
   Empfang brauchte. Nachschlagen geht
   nicht — Verfügbarkeit, Preise und Angebote stehen in der Datenbank,
   und die ist nicht im Rucksack. Der Schirm sagt das, statt leere
   Listen zu zeigen.

   ── Die vier Regeln ───────────────────────────────────────────────

   1. **Die UUID entsteht im Browser, bevor gesendet wird.** Sie geht bei
      jedem Versuch mit (`erfassung_id`), und der Server nimmt dieselbe
      zweimal an, ohne doppelt anzulegen (Migration 0113). Ohne sie wäre
      ein Funkloch mitten in der Antwort ein zweites Protokoll.
   2. **Der Zeitpunkt ist der der Erfassung, nicht der der Übertragung.**
      Sonst verschiebt sich der Mietbeginn um einen Tag, weil jemand
      abends erst wieder ins Netz kommt.
   3. **Dateien zuerst, dann der Rumpf.** Ein Foto, das schon abgelegt
      ist, wird beim nächsten Versuch nicht noch einmal hochgeladen: Der
      Pfad wandert in den Rumpf, der Blob fällt aus der Eintragung. Über
      Baustellenfunk ist das der Unterschied zwischen „geht durch" und
      „fängt jedes Mal von vorn an".
   4. **Ein fachliches Nein hört auf.** `409 protokoll.ausgabe_fehlt`
      wird beim hundertsten Versuch nicht wahrer. Die Eintragung bleibt
      stehen, mit ihrem Grund, und wartet auf einen Menschen — statt
      still zu verschwinden oder im Kreis zu laufen.

   **Eine Eintragung nach der anderen, in der Reihenfolge der Aufnahme.**
   Das Ausstempeln darf nicht vor dem Einstempeln ankommen, und die
   Vorbereitung nicht nach der Übergabe, die sie zeigen soll.
   ══════════════════════════════════════════════════════════════════ */

export type Warteart =
  | "protokoll" | "vorbereitung" | "einstempeln" | "ausstempeln" | "quittung"
  /* Seit dem Einsatzschirm (17.09.2026): losfahren und „Auf der Baustelle"
     (`phase`), der Zählerstand des Tages (`zaehler`) und das Foto als Zeile
     (`foto`, 0120). Dieselben Namen wie `client_entry.kind` auf dem Server —
     „Auf der Baustelle" heißt dort `einstempeln`, hier `phase`, weil es für
     den Fahrer ein Schritt im Einsatz ist und kein Stempel. */
  | "phase" | "zaehler" | "foto"
  /* Der Schaden bei der Rücknahme (0125). Er war die letzte Eintragung des
     Einsatzes, die Netz brauchte: Ohne Empfang blieb der Text im
     Protokollfeld liegen, und in der Liste der offenen Schäden stand keine
     Zeile. Die Fotos gehen vor ihm hinaus — sie stehen früher in der Reihe. */
  | "defekt"
  /* Zeit nachtragen und korrigieren (0126). Sie gingen bis dahin nur am
     Schreibtisch und nur mit Netz — und fallen doch dort auf, wo es keins
     gibt: auf der Baustelle, wenn der Fahrer „Anfahrt" gedrückt hat und
     schon da ist. `zeitkorrektur` geht als `POST /zeiten/korrektur`, weil
     die Warteschlange kein `PATCH` trägt; dahinter liegt derselbe Dienst. */
  | "zeitnachtrag" | "zeitkorrektur";

/** Eine Datei, die noch hinaufgehört — Foto oder Unterschrift. */
type Anhang = {
  /** Wohin der Pfad im Rumpf gehört — `pfad` beim Foto als Zeile (`POST /fotos`). */
  feld: "fotos" | "unterschrift_url" | "pfad";
  zweck: "foto" | "unterschrift";
  blob: Blob;
};

export type Wartender = {
  /** Die UUID vom Gerät — dieselbe, die der Server als `erfassung_id` sieht. */
  id: string;
  art: Warteart;
  /** Wann am Gerät aufgenommen, als ISO-Zeitpunkt. */
  erfasstAm: string;
  /** In Millisekunden — die Reihenfolge, in der übertragen wird. */
  reihe: number;
  /** Was im Band dasteht: „RL-07 · Bauer GmbH". Kein Satz, ein Bezug. */
  betreff: string;
  /** Der Weg, ohne `/api`. Steht mit in der Eintragung, damit sie sich selbst trägt. */
  weg: string;
  /** Der Rumpf. Die Pfade hochgeladener Dateien kommen hier hinein. */
  nutzlast: Record<string, unknown>;
  dateien: Anhang[];
  versuche: number;
  /** Warum es zuletzt nicht ging — ein i18n-Pfad, nie ein Satz. */
  grund: string | null;
  /**
   * Die Werte für den Satz des Grunds — `{maschine}`, `{datum}` (D-25,
   * 03.10.2026). Ohne sie stand „{maschine} hat einen offenen Defekt … vom
   * {datum}" rot im Band, obwohl die API beides geschickt hatte.
   */
  grundWerte?: Record<string, string | number>;
  /** Fachlich abgewiesen: Das Nachfassen hört auf, die Eintragung bleibt. */
  abgewiesen: boolean;
  /**
   * **Nicht vor diesem Zeitpunkt hinaus** (Millisekunden) — das Fenster für
   * „Rückgängig" (17.09.2026). Solange es läuft, steht die Eintragung auf dem
   * Gerät und lässt sich zurücknehmen, ohne dass der Server je davon hört.
   * Was danach kommt, wartet dahinter: Die Reihenfolge bleibt.
   */
  nichtVor?: number;
  /**
   * Unter welchem Namen der Zeitpunkt des Geräts mitgeht — **aber nur, wenn
   * die Eintragung spät hinausgeht** (siehe `SPAET_MS`). Geht sie gleich nach
   * dem Rückgängig-Fenster durch, zählt `now()` der Datenbank wie bei jedem
   * Ruf mit Netz, und die Spur trägt kein „offline".
   */
  zeitfeldSpaet?: string;
};

/* ══════════════ Der Stand, für die Oberfläche ══════════════ */

let stand: Wartender[] = [];
let geladen = false;
let laeuft = false;
/** Kam während eines Zugs etwas dazu? Dann fährt er gleich noch einmal. */
let nochmal = false;
let uhr: number | undefined;
/** Die Eintragung, die gerade hinausgeht — für „lädt" am Foto. */
let aktiv: string | null = null;
/** Der Wecker für das Ende eines Rückgängig-Fensters. */
let haltUhr: number | undefined;
/**
 * **Was gerade hinausgegangen ist** — eine Minute lang, im Speicher.
 *
 * Ein Schirm, der aus Server und Gerät zusammensetzt, was gilt
 * (`daten/einsatzlage.ts`), sprang sonst zurück: Die Eintragung ist aus der
 * Warteschlange weg, die frische Antwort des Servers aber noch nicht da. Für
 * diese Lücke steht sie hier, mit dem Zeitpunkt, zu dem sie ankam.
 */
export type Gesendet = Wartender & { gesendetAm: number };
let gesendet: Gesendet[] = [];
const KEIN_GESENDET: Gesendet[] = [];
const hoerer = new Set<() => void>();
/** Wer erfahren will, dass etwas angekommen ist — die Masken frischen dann auf. */
const nachUebertragung = new Set<() => void>();

function melden(): void {
  hoerer.forEach((h) => h());
}

async function neuLesen(): Promise<void> {
  const liste = await alle<Wartender>(FACH_WARTESCHLANGE);
  stand = liste.sort((a, b) => a.reihe - b.reihe);
  geladen = true;
  melden();
}

function abonnieren(hoer: () => void): () => void {
  hoerer.add(hoer);
  if (!geladen) void neuLesen();
  return () => hoerer.delete(hoer);
}

const LEER: Wartender[] = [];

/** Was gerade wartet. Leer, solange noch gelesen wird — und das ist der Normalfall. */
export function useWarteschlange(): Wartender[] {
  return useSyncExternalStore(abonnieren, () => stand, () => LEER);
}

/** Was in der letzten Minute angekommen ist (siehe `gesendet`). */
export function useGesendet(): Gesendet[] {
  return useSyncExternalStore(abonnieren, () => gesendet, () => KEIN_GESENDET);
}

/** Welche Eintragung gerade hinausgeht — oder `null`. */
export function useUebertraegtGerade(): string | null {
  return useSyncExternalStore(abonnieren, () => aktiv, () => null);
}

/* ══════════════ Einreihen ══════════════ */

/** Kein Netz — dann wird gar nicht erst versucht, sondern eingereiht. */
export const ohneNetz = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Ein Fehler, der am Netz liegt und nicht an der Eintragung.
 *
 * `netz` wirft `klient.ts`, wenn `fetch` gar nicht erst durchkam. `401` und
 * `429` sind keine fachlichen Neins: Das eine heißt „melde dich neu an", das
 * andere „nicht so schnell". `5xx` ist unsere Seite.
 */
export function netzfehler(e: unknown): boolean {
  if (!(e instanceof Fehler)) return true;
  return e.schluessel === "netz" || e.status === 0 || e.status === 401
    || e.status === 429 || e.status >= 500;
}

export type Einreihung = {
  art: Warteart;
  weg: string;
  betreff: string;
  nutzlast: Record<string, unknown>;
  dateien?: Anhang[];
  /** Vorgegeben nur dort, wo die Maske die UUID schon kennt (Stempeluhr). */
  id?: string;
  erfasstAm?: Date;
  /** Wie lange sie zurückgenommen werden kann, bevor sie hinausgeht (ms). */
  halten?: number;
  /** Der Zeitpunkt des Geräts geht nur mit, wenn es spät wird (`Wartender`). */
  zeitfeldSpaet?: string;
};

/* Die Reihenfolge der Aufnahme — streng steigend, auch wenn zwei Eintragungen
   in derselben Millisekunde entstehen (Vorbereitung und Protokoll bei der
   Übergabe, 18.09.2026): Die zweite darf nie vor der ersten hinausgehen. */
let letzteReihe = 0;
const naechsteReihe = (): number => {
  letzteReihe = Math.max(Date.now(), letzteReihe + 1);
  return letzteReihe;
};

/** Legt eine Eintragung auf das Gerät. Gibt die UUID zurück — sie ist der Bezug. */
export async function einreihen(e: Einreihung): Promise<string> {
  const eintrag: Wartender = {
    id: e.id ?? uuid(),
    art: e.art,
    erfasstAm: (e.erfasstAm ?? new Date()).toISOString(),
    reihe: naechsteReihe(),
    betreff: e.betreff,
    weg: e.weg,
    nutzlast: e.nutzlast,
    dateien: e.dateien ?? [],
    versuche: 0,
    grund: null,
    abgewiesen: false,
    ...(e.halten ? { nichtVor: Date.now() + e.halten } : {}),
    ...(e.zeitfeldSpaet ? { zeitfeldSpaet: e.zeitfeldSpaet } : {}),
  };
  await schreiben(FACH_WARTESCHLANGE, eintrag);
  await neuLesen();
  // Gleich versuchen: Wer gerade wieder Empfang hat, soll nicht auf den
  // nächsten Takt warten. Ohne Netz kostet es einen Blick auf `onLine`.
  void uebertragen();
  return eintrag.id;
}

/**
 * Der eine Weg, den jede Maske am Telefon geht: **erst senden, sonst einreihen.**
 *
 * Die UUID entsteht **vor** dem ersten Versuch. Bricht die Leitung mitten in
 * der Antwort ab, geht dieselbe UUID später noch einmal hinaus, und der Server
 * legt nichts doppelt an (Migration 0113) — das ist der ganze Grund, warum sie
 * nicht erst beim Einreihen erzeugt wird.
 *
 * `zeitfeld` sagt, unter welchem Namen der Zeitpunkt des Geräts mitgeht, wenn
 * eingereiht wird. Mit Netz geht er **nicht** mit: Dann zählt `now()` der
 * Datenbank, und das ist die bessere Zahl (09-mobile.md, Stempeluhr).
 *
 * Ein fachliches Nein geht durch wie immer — die Maske zeigt es, und niemand
 * reiht einen Fehler in eine Warteschlange ein.
 */
export async function sendeOderEinreihen(e: Einreihung & { zeitfeld?: string; nurEinreihen?: boolean }): Promise<{
  eingereiht: boolean;
  antwort: Record<string, unknown> | null;
}> {
  const id = e.id ?? uuid();
  const erfasstAm = e.erfasstAm ?? new Date();
  const { nurEinreihen, ...eintrag } = e;
  // Liegen noch Bilder als Blob dabei, ist das Ablegen vorhin schon
  // gescheitert. Dann wird nicht gesendet, sondern eingereiht — die
  // Warteschlange legt sie nacheinander ab (Regel 3). **`nurEinreihen`**:
  // Liegt die Eintragung davor schon auf dem Gerät, darf diese nicht an ihr
  // vorbei (die Übergabe hinter ihrer nachgezogenen Vorbereitung).
  if (!nurEinreihen && !ohneNetz() && !e.dateien?.length) {
    try {
      const antwort = await sende<Record<string, unknown>>(e.weg, {
        ...e.nutzlast,
        erfassung_id: id,
      });
      return { eingereiht: false, antwort };
    } catch (fehler) {
      // Die Übergabe steht schon (D-20) — das ist ein Ja, kein Nein.
      if (fehler instanceof Fehler && stehtSchon(fehler, e.art)) {
        return { eingereiht: false, antwort: { schon_da: true } };
      }
      if (!netzfehler(fehler)) throw fehler;
    }
  }
  await einreihen({
    ...eintrag,
    id,
    erfasstAm,
    nutzlast: e.zeitfeld
      ? { ...e.nutzlast, [e.zeitfeld]: erfasstAm.toISOString() }
      : e.nutzlast,
  });
  return { eingereiht: true, antwort: null };
}

/**
 * Der Haken für die Masken am Telefon — Protokoll und Vorbereitung.
 *
 * **Danach ist alles alt**, und deshalb wird alles für ungültig erklärt statt
 * einer Liste von Schlüsseln: Ein Protokoll setzt das Ist an der Belegung,
 * stellt den Vorgang um und ändert die Tagesliste, die Startseite, das Brett
 * und die Fotos der Maschine. Auf einem Telefon sind das drei Abfragen — eine
 * vergessene wäre eine Zeile, die nach der Übergabe noch unter RAUS steht.
 */
export function useAufnehmen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: (e: Einreihung & { zeitfeld?: string; nurEinreihen?: boolean }) => sendeOderEinreihen(e),
    onSuccess: () => void kunde.invalidateQueries(),
  });
}

/**
 * Eine Eintragung zurücknehmen, bevor sie hinausgeht — „Rückgängig".
 *
 * **Nur, was noch auf dem Gerät liegt.** Geht sie gerade hinaus oder ist sie
 * schon weg, kommt `false`: Dann ist sie beim Server, und zurück geht es nur
 * über einen Menschen mit Grund (die Disposition am Vorgang).
 */
export async function zuruecknehmen(id: string): Promise<boolean> {
  if (aktiv === id) return false;
  if (!stand.some((w) => w.id === id)) return false;
  await entfernen(FACH_WARTESCHLANGE, id);
  await neuLesen();
  return true;
}

/** Das Ende einer Schicht, deren Anfang noch im Gerät liegt (Schicht über Mitternacht). */
export async function endeNachtragen(eintragId: string, ende: Date): Promise<boolean> {
  const eintrag = stand.find((w) => w.id === eintragId);
  if (!eintrag || eintrag.art !== "einstempeln" || eintrag.nutzlast["ende"]) return false;
  const neu: Wartender = {
    ...eintrag,
    nutzlast: { ...eintrag.nutzlast, ende: ende.toISOString() },
    grund: null,
    abgewiesen: false,
  };
  await schreiben(FACH_WARTESCHLANGE, neu);
  await neuLesen();
  void uebertragen();
  return true;
}

/* ══════════════ Übertragen ══════════════ */

/** Legt die Dateien ab und schreibt ihre Pfade in den Rumpf (Regel 3). */
async function dateienAblegen(eintrag: Wartender): Promise<Wartender> {
  let jetzt = eintrag;
  while (jetzt.dateien.length > 0) {
    const [erste, ...rest] = jetzt.dateien as [Anhang, ...Anhang[]];
    const pfad = await ablageHochladen(erste.blob, erste.zweck);
    const nutzlast = { ...jetzt.nutzlast };
    if (erste.feld === "fotos") {
      const bisher = Array.isArray(nutzlast["fotos"]) ? (nutzlast["fotos"] as string[]) : [];
      nutzlast["fotos"] = [...bisher, pfad];
    } else {
      nutzlast[erste.feld] = pfad;
    }
    jetzt = { ...jetzt, nutzlast, dateien: rest };
    // Nach jeder Datei gesichert: Bricht die Leitung beim dritten Foto ab,
    // gehen die ersten beiden nicht noch einmal hinauf.
    await schreiben(FACH_WARTESCHLANGE, jetzt);
  }
  return jetzt;
}

/**
 * Ab wann eine Eintragung als „spät" gilt: eine Minute nach der Aufnahme —
 * oder sobald ein Versuch am Netz gescheitert ist. Dann war das Gerät ohne
 * Netz, und der Zeitpunkt des Geräts ist die bessere Zahl.
 */
const SPAET_MS = 60_000;

async function einenUebertragen(eintrag: Wartender): Promise<boolean> {
  try {
    const fertig = await dateienAblegen(eintrag);
    const spaet = !!fertig.zeitfeldSpaet
      && (fertig.versuche > 0 || Date.now() - new Date(fertig.erfasstAm).getTime() > SPAET_MS);
    await sende(fertig.weg, {
      ...fertig.nutzlast,
      ...(spaet && fertig.zeitfeldSpaet ? { [fertig.zeitfeldSpaet]: fertig.erfasstAm } : {}),
      erfassung_id: fertig.id,
    });
    await entfernen(FACH_WARTESCHLANGE, fertig.id);
    const jetzt = Date.now();
    gesendet = [...gesendet.filter((g) => jetzt - g.gesendetAm < 60_000), { ...fertig, gesendetAm: jetzt }];
    return true;
  } catch (e) {
    /* **Die Übergabe steht schon** (Playbook D-20). Kam sie zweimal an —
       mit zwei Kennungen, aus der Zeit vor dem 03.10.2026 oder von zwei
       Geräten —, ist die zweite erledigt und keine Abweisung: kein rotes
       Band, keine Meldung an die Disposition. */
    if (e instanceof Fehler && stehtSchon(e, eintrag.art)) {
      await entfernen(FACH_WARTESCHLANGE, eintrag.id);
      return true;
    }
    const amNetz = netzfehler(e);
    const grund = e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
    const grundWerte = e instanceof Fehler ? e.werte : {};
    await schreiben(FACH_WARTESCHLANGE, {
      ...eintrag,
      versuche: eintrag.versuche + 1,
      grund,
      grundWerte,
      abgewiesen: !amNetz,
    } satisfies Wartender);
    /* **Ein fachliches Nein bleibt nicht auf diesem Telefon** (24.09.2026,
       Paket 8): Es geht als Meldung an den, der erfasst hat, und an die
       Disposition. Nur beim ersten Mal; scheitert die Meldung selbst, bleibt
       es beim Band auf dem Gerät. */
    if (!amNetz && !eintrag.abgewiesen) {
      const belegung = typeof eintrag.nutzlast["belegung_id"] === "string" ? eintrag.nutzlast["belegung_id"] : null;
      void sende("/meldungen/abgewiesen", {
        erfassung_id: eintrag.id, betreff: eintrag.betreff, grund,
        ...(Object.keys(grundWerte).length ? { grund_werte: grundWerte } : {}),
        ...(belegung ? { belegung_id: belegung } : {}),
      }).catch(() => {});
    }
    return false;
  }
}

/**
 * Alles hinaus, was hinaus kann.
 *
 * **Nacheinander, und beim ersten Netzfehler Schluss.** Zehn Eintragungen
 * einzeln gegen ein totes Netz laufen zu lassen, kostet zehn Zeitüberschreitungen
 * und bringt nichts. Ein fachliches Nein hält den Zug dagegen nicht auf: Die
 * Eintragung bleibt mit ihrem Grund stehen, die nächste geht weiter.
 */
export async function uebertragen(): Promise<void> {
  /* **Läuft der Zug schon, fährt er danach noch einmal** (17.09.2026). Er geht
     eine Abschrift der Warteschlange durch; was währenddessen dazukam — das
     Ende des Tages, gleich danach die Bestätigung —, stand nicht darin und
     wartete bis zum Takt der nächsten Minute. Am Telefon hieß das: „Danke",
     und die Bestätigung war eine Minute lang nirgends. */
  if (laeuft) {
    nochmal = true;
    return;
  }
  if (ohneNetz()) return;
  laeuft = true;
  nochmal = false;
  try {
    if (!geladen) await neuLesen();
    let etwasGing = false;
    for (const eintrag of [...stand]) {
      if (eintrag.abgewiesen) continue;
      // Zurückgenommen, während der Zug lief — dann geht sie nicht hinaus.
      if (!stand.some((w) => w.id === eintrag.id)) continue;
      /* **Das Rückgängig-Fenster läuft noch.** Dann hält der Zug hier an, und
         was dahinter liegt, wartet mit: Die Ankunft darf nicht vor der
         Anfahrt ankommen. Ein Wecker stößt ihn wieder an. */
      if (eintrag.nichtVor && eintrag.nichtVor > Date.now()) {
        window.clearTimeout(haltUhr);
        haltUhr = window.setTimeout(() => void uebertragen(), eintrag.nichtVor - Date.now() + 50);
        break;
      }
      aktiv = eintrag.id;
      melden();
      let ging = false;
      try {
        ging = await einenUebertragen(eintrag);
      } finally {
        aktiv = null;
        melden();
      }
      if (ging) etwasGing = true;
      else if (!stand.find((w) => w.id === eintrag.id)?.abgewiesen) break;
    }
    await neuLesen();
    if (etwasGing) nachUebertragung.forEach((h) => h());
  } finally {
    laeuft = false;
  }
  if (nochmal) {
    nochmal = false;
    void uebertragen();
  }
}

/** „Jetzt versuchen" — auch das, was schon abgewiesen war. Ein Mensch drückt. */
export async function nochEinmalVersuchen(): Promise<void> {
  for (const eintrag of stand.filter((w) => w.abgewiesen)) {
    await schreiben(FACH_WARTESCHLANGE, { ...eintrag, abgewiesen: false, grund: null, grundWerte: {} });
  }
  await neuLesen();
  await uebertragen();
}

/**
 * Den Takt anwerfen: bei `online`, beim Wiederaufwachen und jede Minute,
 * solange etwas wartet.
 *
 * Eine Minute ist kein Kompromiss, sondern die Beobachtung: Empfang kommt
 * auf der Rückfahrt wieder, nicht auf Zuruf. `online` allein reicht nicht —
 * der Browser meldet es auch, wenn nur das WLAN wieder da ist, hinter dem
 * niemand ist.
 */
export function warteschlangeStarten(danach?: () => void): void {
  if (danach) nachUebertragung.add(danach);
  void neuLesen().then(() => void uebertragen());
  window.addEventListener("online", () => void uebertragen());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void uebertragen();
  });
  window.clearInterval(uhr);
  uhr = window.setInterval(() => {
    if (stand.some((w) => !w.abgewiesen)) void uebertragen();
  }, 60_000);
}
